/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { AlwaysLightMode, Typography } from '@coursera/cds-core';

import getAccessibleTextColor from 'bundles/enterprise-legacy-learner-home/utils/getAccessibleTextColor';

type ThirdPartyOrganization = {
  name?: string | null;
  primaryColor?: string | null;
  iconColor?: string | null;
};

type LogoShape = 'circle' | 'square';

type DefaultOrganizationProps = {
  size?: number;
  /**
   * Visual shape of the logo background.
   * - 'circle' (default): fully rounded — used historically for legacy learner-home avatars.
   * - 'square': rounded square, matching the modern admin sidenav / org switcher design.
   */
  shape?: LogoShape;
  thirdPartyOrganization: ThirdPartyOrganization;
};

const DefaultOrganizationLogo = ({
  size = 48,
  shape = 'circle',
  thirdPartyOrganization: { name, primaryColor, iconColor },
}: DefaultOrganizationProps) => {
  const backgroundColor = iconColor || primaryColor || '#00255d'; // must be a hex code for getAccessibleTextColor
  const darkTextColor = '#1f1f1f'; // must be a hex code for getAccessibleTextColor
  const lightTextColor = '#ffffff'; // must be a hex code for getAccessibleTextColor
  const accessibleTextColor = getAccessibleTextColor(backgroundColor, darkTextColor, lightTextColor);
  const typographyColor = accessibleTextColor === darkTextColor ? 'body' : 'invertBody';

  const borderRadius = shape === 'circle' ? '50%' : 'var(--cds-border-radius-50)';

  const styles = {
    iconBackground: css`
      width: ${size}px;
      height: ${size}px;
      background-color: ${backgroundColor};
      border-radius: ${borderRadius};
      display: flex;
      align-items: center;
      text-align: center;
    `,
    iconText: css`
      width: 100%;

      /* Need to use an ID on the Typography component to override the CDS font size. */
      &#defaultOrganizationLogoIconText {
        font-size: ${size * 0.65}px;
      }
    `,
  };

  return (
    <AlwaysLightMode>
      <div css={styles.iconBackground} aria-label={name || ''}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography
          color={typographyColor}
          variant="h1semibold"
          component="span"
          css={styles.iconText}
          id="defaultOrganizationLogoIconText"
          data-testid="defaultOrganizationLogoIconText"
        >
          {name?.charAt(0).toLocaleUpperCase()}
        </Typography>
      </div>
    </AlwaysLightMode>
  );
};

export default DefaultOrganizationLogo;
